import { useEffect, useState } from 'react'
import {
  canOfferInstall,
  canPromptNative,
  isInAppBrowser,
  isIos,
  isStandalone,
  markPrompted,
  promptNativeInstall,
  realBrowserName,
  shouldAutoPrompt,
  subscribe,
} from '../lib/pwaInstall'
import IosInstallSteps from './IosInstallSteps'
import CopyLinkButton from './CopyLinkButton'

function useInstallState() {
  const [, force] = useState(0)
  useEffect(() => subscribe(() => force((n) => n + 1)), [])
  return {
    native: canPromptNative(),
    ios: isIos(),
    installed: isStandalone(),
    offer: canOfferInstall(),
    inApp: isInAppBrowser(),
  }
}

// Bottom banner shown ~1s after a successful order, and on /my-orders for
// returning customers. Never shows when the app is already installed.
export default function InstallPrompt() {
  const { native, ios, inApp } = useInstallState()
  const [visible, setVisible] = useState(false)
  const [iosHelp, setIosHelp] = useState(false)

  useEffect(() => {
    // The native event can land a moment after mount — re-check until then.
    const t = setTimeout(() => { if (shouldAutoPrompt()) setVisible(true) }, 1000)
    return () => clearTimeout(t)
  }, [native])

  if (iosHelp) {
    return <IosInstallModal onClose={() => { setIosHelp(false); setVisible(false) }} />
  }
  if (!visible) return null

  function notNow() {
    markPrompted('dismissed')
    setVisible(false)
  }

  // Instagram / WhatsApp / Gmail & co: no install is possible here, so we
  // never show a button that cannot work. We show the way out instead.
  if (inApp) {
    return (
      <Banner onClose={notNow}>
        <div className="font-bricolage text-[14px] font-extrabold text-navy">
          📱 To install Ghost Laundry, open this page in {realBrowserName()}
        </div>
        <div className="mt-0.5 text-[12px] text-ink-2">
          This in-app browser can&apos;t add apps to your home screen.
        </div>
        <div className="mt-3 flex items-center gap-4">
          <CopyLinkButton />
          <button type="button" onClick={notNow} className="text-[12px] font-bold text-ink-2">
            Not now
          </button>
        </div>
      </Banner>
    )
  }

  if (!native && !ios) return null

  async function install() {
    if (native) {
      await promptNativeInstall()
      setVisible(false)
    } else {
      // They TAPPED INSTALL — that is interest, not a refusal. Recording a
      // dismissal here is what hid the banner for 14 days from the customer
      // whose install failed. 'shown_ios' comes back in 3 days instead.
      markPrompted('shown_ios')
      setIosHelp(true)
    }
  }

  return (
    <Banner onClose={notNow}>
      <div className="font-bricolage text-[14px] font-extrabold text-navy">
        📱 Save Ghost Laundry to your home screen
      </div>
      <div className="mt-0.5 text-[12px] text-ink-2">
        1-tap orders — no more searching for our site.
      </div>
      <div className="mt-3 flex items-center gap-4">
        <button
          type="button"
          onClick={install}
          className="rounded-lg bg-navy px-4 py-2 text-[12px] font-extrabold text-white"
        >
          Install
        </button>
        <button type="button" onClick={notNow} className="text-[12px] font-bold text-ink-2">
          Not now
        </button>
      </div>
    </Banner>
  )
}

// Shared shell: bottom sheet, logo, close button. Dismissible — never a
// modal that blocks the page.
function Banner({ onClose, children }) {
  return (
    <div
      className="pointer-events-none fixed inset-x-0 z-40 flex justify-center px-3"
      style={{ bottom: 'calc(68px + env(safe-area-inset-bottom))' }}
    >
      <div className="gl-slide-up pointer-events-auto relative w-full max-w-md rounded-2xl border border-line bg-white p-4 pr-10 shadow-lg">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-3 top-3 text-[16px] text-ink-3 hover:text-ink"
        >
          ✕
        </button>
        <div className="flex items-start gap-3">
          <img
            src="/logo-ghost.png"
            alt=""
            aria-hidden="true"
            width="44"
            height="44"
            className="h-11 w-11 flex-shrink-0 rounded-xl"
          />
          <div className="min-w-0 flex-1">{children}</div>
        </div>
      </div>
    </div>
  )
}

// "📱 Get the app" — for the account page. Hidden once installed or when the
// browser offers no install path at all (e.g. desktop Firefox).
export function InstallAppButton({ className = '' }) {
  const { native, ios, installed, offer } = useInstallState()
  const [iosHelp, setIosHelp] = useState(false)
  if (installed || !offer) return null

  async function onClick() {
    if (native) await promptNativeInstall()
    else if (ios) setIosHelp(true)
  }

  return (
    <>
      <button
        type="button"
        onClick={onClick}
        className={`flex w-full items-center justify-between rounded-xl border border-line bg-white px-4 py-3 text-left ${className}`}
      >
        <span>
          <span className="block text-[13px] font-extrabold text-navy">📱 Get the app</span>
          <span className="block text-[11px] text-ink-2">Add Ghost Laundry to your home screen</span>
        </span>
        <span className="text-[12px] font-extrabold text-aqua">Install →</span>
      </button>
      {iosHelp && <IosInstallModal onClose={() => setIosHelp(false)} />}
    </>
  )
}

function IosInstallModal({ onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-3 sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-label="Install Ghost Laundry"
      onClick={onClose}
    >
      <div
        className="gl-slide-up relative w-full max-w-sm rounded-2xl bg-white p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-4 top-4 text-[16px] text-ink-3 hover:text-ink"
        >
          ✕
        </button>
        <div className="flex items-center gap-3">
          <img src="/logo-ghost.png" alt="" aria-hidden="true" width="44" height="44" className="h-11 w-11 rounded-xl" />
          <div className="font-bricolage text-[16px] font-extrabold text-navy">Add to your home screen</div>
        </div>

        <div className="mt-4">
          <IosInstallSteps />
        </div>

        <div className="mt-3 text-center text-[20px] text-aqua" aria-hidden>↓</div>
        <p className="text-center text-[11px] text-ink-3">The Share button is in Safari&apos;s bottom bar</p>

        <button
          type="button"
          onClick={onClose}
          className="mt-4 w-full rounded-xl bg-navy py-3 text-[13px] font-extrabold text-white"
        >
          Got it
        </button>
      </div>
    </div>
  )
}
